import { makeStyles } from "tss-react/mui"
import { blueGrey, lightBlue, red } from "../../../../mp-core/themes/palette"
import {
  alpha,
  inputClasses,
  inputLabelClasses,
  autocompleteClasses,
  typographyClasses,
  formHelperTextClasses
} from "@mui/material"

type StylesParam = {
  error?: boolean
  inline?: boolean
  variant?: "filled" | "outlined"
  withAdormentBorder?: boolean
}

export type StyleName =
  | "container"
  | "control"
  | "placeholder"
  | "adornmentWrapper"
  | "endAdornment"
  | "popupIndicator"
  | "clearIndicator"
  | "tag"
  | "popper"
  | "paper"
  | "listbox"
  | "item"
  | "itemSelected"
  | "checkbox"
  | "tagsWrapper"
  | "helperText"

export const useStyles = makeStyles<StylesParam, StyleName>()(
  (
    { spacing, palette, shadows, breakpoints, mixins },
    {
      withAdormentBorder = true,
      inline = false,
      error = false,
      variant = "outlined"
    },
    classes
  ) => ({
    container: {
      [`&:has(.${inputLabelClasses.root})`]: {
        [`& .${inputClasses.root}`]: {
          marginTop: 0,
          paddingTop: spacing(5),
          paddingBottom: spacing(2),

          ...(inline && {
            // Split into longhand so components trying to set padding on autocomplete can
            paddingBlock: spacing(2)
          }),

          ...(error && {
            border: `1px solid ${red[400]}`,
            outline: `3px solid ${alpha(red[300], 0.2)}`
          })
        },

        [`& .${inputClasses.input}`]: {
          height: "1.375rem",

          [`&.${inputClasses.inputSizeSmall}`]: {
            height: "1rem"
          }
        }
      },

      [`& .${inputClasses.root}`]: {
        border: `1px solid ${blueGrey[200]}`,
        borderRadius: "3px",
        minWidth: "150px",
        padding: spacing(4, 6, 4, 4),
        alignItems: "center",
        backgroundColor: palette.common.white,
        columnGap: spacing(2),
        rowGap: spacing(1),

        ...(inline && {
          // Split into longhand (block/inline) so consumers can override
          // paddingRight without racing the shorthand's expanded value.
          paddingBlock: spacing(2),
          paddingInline: spacing(3),

          [`&:has(.${inputClasses.inputSizeSmall})`]: {
            // Only vertical padding actually changes for the small input
            // size; leave paddingInline to inherit so it doesn't reset
            // paddingRight at a higher specificity.
            paddingBlock: spacing(1)
          }
        }),

        ...(variant === "filled" && {
          backgroundColor: alpha(blueGrey[50], 0.5),
          border: `1px solid ${alpha(blueGrey[50], 0.5)}`,

          [`&:hover:not(.${inputClasses.focused}, .${inputClasses.disabled})`]:
            {
              border: `1px solid ${alpha(blueGrey[50], 0.5)}`
            }
        }),

        ...(variant === "filled" && {
          [`& .${autocompleteClasses.endAdornment}`]: {
            "> div": { background: blueGrey[50] }
          }
        }),

        [`&.${inputClasses.focused}`]: {
          ...mixins.focusRing.outline
        },
        [`&:hover:not(.${inputClasses.focused}, .${inputClasses.disabled})`]: {
          borderColor: blueGrey[400]
        },

        [`&.${inputClasses.disabled}`]: {
          borderColor: blueGrey[100],

          [`& .${classes.adornmentWrapper}`]: {
            borderColor: blueGrey[100]
          },

          ...(variant === "filled" && {
            backgroundColor: blueGrey[50],
            borderColor: blueGrey[50]
          })
        },

        [`& .${inputClasses.input}`]: {
          padding: spacing(0),
          fontSize: "1rem",
          lineHeight: "1.375rem",
          height: "1.625rem",
          boxSizing: "border-box",
          minWidth: 0,

          ...(inline && {
            fontSize: "0.875rem",
            lineHeight: "1rem",
            height: "1.125rem"
          }),

          [`&.${inputClasses.inputSizeSmall}`]: {
            fontSize: "0.875rem",
            lineHeight: "1rem",
            height: "1.125rem",
            padding: spacing(0),

            ...(inline && {
              height: "1.25rem"
            })
          },

          [`&::placeholder`]: {
            color: blueGrey[500],
            opacity: 1
          },

          [`&.${inputClasses.disabled}, &.${inputClasses.disabled}::placeholder`]:
            {
              color: blueGrey[300],
              WebkitTextFillColor: blueGrey[300]
            }
        },

        [`&:has(.${autocompleteClasses.clearIndicator}) .${inputClasses.input}`]:
          {
            marginRight: "20px"
          },

        ...(error && {
          border: `1px solid ${red[400]}`,
          outline: `3px solid ${alpha(red[300], 0.2)}`
        })
      },

      [`& .${inputLabelClasses.root}`]: {
        color: blueGrey[500],
        left: "1px",
        top: spacing(0),
        fontSize: "1rem",
        lineHeight: "1.375rem",
        transform: "translate(16px, 19px) scale(1)",

        ...(inline && {
          fontSize: "0.875rem",
          lineHeight: "1.25rem",
          transform: "translate(12px, 8px) scale(1)"
        }),

        [`& + .${inputClasses.root}`]: {
          marginTop: 0
        },

        [`&.${inputLabelClasses.sizeSmall}`]: {
          fontSize: "0.875rem",
          lineHeight: "1.375rem",
          transform: "translate(16px, 16px) scale(1)",

          ...(inline && {
            transform: "translate(8px, 5px) scale(1)"
          })
        },

        [`&.${inputLabelClasses.shrink}`]: {
          fontSize: "0.75rem",
          lineHeight: "0.875rem",
          transform: "translate(16px, 10px) scale(1)",

          ...(inline && { display: "none" }),

          [`&.${inputLabelClasses.sizeSmall}`]: {
            transform: "translate(16px, 8px) scale(1)"
          }
        }
      },

      [`& .${classes.tag}`]: {
        margin: 0,
        maxWidth: "100%",

        ...(variant === "filled" && {
          backgroundColor: palette.common.white
        })
      }
    },
    control: {
      backgroundColor: palette.common.white,
      borderRadius: "3px"
    },
    placeholder: {
      position: "absolute",
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      padding: spacing(2, 2),

      [`& .${typographyClasses.root}`]: {
        fontSize: "1rem",
        lineHeight: "1.375rem",

        [breakpoints.up("md")]: {
          fontSize: "1rem",
          lineHeight: "1.375rem"
        }
      }
    },
    adornmentWrapper: {
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: spacing(1),

      ":last-of-type": {
        ...(withAdormentBorder && {
          borderLeft: `1px solid ${blueGrey[200]}`,

          ...(variant === "filled" && {
            borderLeft: `1px solid ${blueGrey[100]}`
          })
        }),
        borderTopRightRadius: 2,
        borderBottomRightRadius: 2
      }
    },
    endAdornment: {
      top: 0,
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1
    },
    popupIndicator: {
      margin: 0,
      padding: 0,
      color: blueGrey[500],
      width: spacing(5),
      height: spacing(5),

      [`&.${autocompleteClasses.popupIndicatorOpen}`]: {
        transform: "none"
      }
    },
    clearIndicator: {
      margin: 0,
      padding: "1px", // Should be 0, but line is barely visible then.
      width: spacing(5),
      height: spacing(5),
      visibility: "visible",
      color: blueGrey[400],

      "&:hover": {
        color: blueGrey[500]
      },
      "&:focus": {
        ...mixins.focusRing.ring
      }
    },
    popper: {
      marginTop: spacing(1)
    },
    paper: {
      borderRadius: 2,
      boxShadow: shadows[6]
    },
    listbox: {
      padding: spacing(1, 0)
    },
    tag: {
      margin: 0,
      maxWidth: "100%"
    },
    checkbox: {
      padding: 0,
      color: blueGrey[500],
      "&.Mui-checked": {
        color: lightBlue[500]
      }
    },
    item: {
      padding: spacing(2, 3),
      cursor: "pointer",
      flexDirection: "row",
      alignItems: "center",
      gap: spacing(2),

      "&:hover, &.Mui-focused": {
        backgroundColor: "#ECEEF1"
      }
    },
    itemSelected: {
      backgroundColor: lightBlue[50]
    },
    tagsWrapper: {
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      columnGap: spacing(2),
      rowGap: spacing(1)
    },
    helperText: {
      margin: spacing(2, 4, 0),
      color: lightBlue[800],

      [`&.${formHelperTextClasses.error}`]: {
        color: red[700]
      }
    }
  })
)
